import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Try to get cached user from localStorage for instant UI
    const cached = localStorage.getItem("cached_user");
    return cached ? JSON.parse(cached) : null;
  });
  const [token, setToken] = useState(localStorage.getItem("auth_token"));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem("refresh_token"));
  const [isLoading, setIsLoading] = useState(!localStorage.getItem("cached_user"));

  const fetchUser = useCallback(async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      // Cache user for instant load next time
      localStorage.setItem("cached_user", JSON.stringify(response.data));
    } catch (error) {
      console.error("Failed to fetch user:", error);
      // Clear invalid session
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("cached_user");
      setToken(null);
      setRefreshToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Only fetch if we have a token but no cached user, or to validate cached user
    if (token) {
      fetchUser();
    } else {
      setIsLoading(false);
    }
  }, [token, fetchUser]);

  // Token refresh interval (every 20 minutes)
  useEffect(() => {
    if (!refreshToken) return;
    
    const interval = setInterval(async () => {
      try {
        const response = await axios.post(`${API}/auth/refresh`, {
          refresh_token: refreshToken
        });
        localStorage.setItem("auth_token", response.data.access_token);
        setToken(response.data.access_token);
      } catch (error) {
        console.error("Token refresh failed:", error);
        logout();
      }
    }, 20 * 60 * 1000); // 20 minutes
    
    return () => clearInterval(interval);
  }, [refreshToken]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { user: userData, access_token, refresh_token } = response.data;
    
    localStorage.setItem("auth_token", access_token);
    localStorage.setItem("cached_user", JSON.stringify(userData));
    if (refresh_token) {
      localStorage.setItem("refresh_token", refresh_token);
      setRefreshToken(refresh_token);
    }
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (email, password, name, organization) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      organization
    });
    const { user: userData, access_token, refresh_token } = response.data;
    
    localStorage.setItem("auth_token", access_token);
    localStorage.setItem("cached_user", JSON.stringify(userData));
    if (refresh_token) {
      localStorage.setItem("refresh_token", refresh_token);
      setRefreshToken(refresh_token);
    }
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("cached_user");
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  };

  const logoutAllSessions = async () => {
    try {
      await axios.post(`${API}/auth/logout-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Logout all error:", error);
    }
    logout();
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${API}/auth/forgot-password`, { email });
    return response.data;
  };

  const resetPassword = async (resetToken, newPassword) => {
    const response = await axios.post(`${API}/auth/reset-password`, {
      token: resetToken,
      new_password: newPassword
    });
    return response.data;
  };

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
    login,
    register,
    logout,
    logoutAllSessions,
    forgotPassword,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
